import '../auth.scss';

import { acceptInvite, getInviteDetails } from '@api/team';
import { ReactComponent as Logo } from '@basqet-dashboard/common/assets/logo.svg';
import { Button, FormInput } from '@basqet-dashboard/common/components';
import { APP_NAME } from '@basqet-dashboard/common/config';
import { useDocumentTitle } from '@basqet-dashboard/common/hooks';
import { onboardingSchema } from '@basqet-dashboard/common/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

function Onboarding() {
  const { token } = useParams();
  const history = useHistory();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);

  useDocumentTitle('Join Basqet');

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const data = await getInviteDetails(token);
        setInvite(data);
      } catch (err) {
        toast.error('Invalid or expired invitation link.');
        history.push('/signup');
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchInvite();
  }, [token, history]);

  if (loading) {
    return (
      <div className="auth__content">
        <div className="auth__stage">
          <div className="auth__stage__half" />
        </div>
        <div className="auth__card">
          <Logo />
          <main className="auth__loading">
            <h3>Loading invitation…</h3>
          </main>
        </div>
      </div>
    );
  }

  if (!invite) {
    return null;
  }

  return (
    <main className="auth">
      <OnboardingForm invite={invite} />
    </main>
  );
}

const OnboardingForm = ({ invite }) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(onboardingSchema),
    defaultValues: {
      email: invite.email,
      first_name: invite.first_name,
      last_name: invite.last_name,
      password: '',
      confirm_password: ''
    }
  });

  const onSubmit = async (body) => {
    setIsLoading(true);
    try {
      await acceptInvite({
        token: invite.token,
        password: body.password,
        confirm_password: body.confirm_password
      });
      toast.success('Account activated successfully. You can now log in.');
      history.push('/login');
    } catch (err) {
      toast.error('Unable to complete onboarding.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth__content">
      <div className="auth__stage">
        <div className="auth__stage__half" />
      </div>

      <h1 className="page__title">
        Join <strong>{invite.merchant}</strong> on {APP_NAME}
      </h1>

      <div className="auth__card">
        <Logo />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="auth__card__input-area">
            <FormInput
              label="Email"
              type="email"
              error={errors.email?.message}
              register={register('email')}
              readOnly
            />
            <FormInput
              label="First Name"
              type="text"
              error={errors.first_name?.message}
              register={register('first_name')}
              readOnly
            />
            <FormInput
              label="Last Name"
              type="text"
              error={errors.last_name?.message}
              register={register('last_name')}
              readOnly
            />
            <FormInput
              label="Password"
              placeholder="Enter your password"
              type="password"
              error={errors.password?.message}
              {...register('password')}
            />
            <FormInput
              label="Confirm Password"
              placeholder="Confirm your password"
              type="password"
              error={errors.confirm_password?.message}
              {...register('confirm_password')}
            />
          </div>

          <Button isFull isLoading={isLoading} disabled={isLoading}>
            Join {invite.merchant}
          </Button>
        </form>
      </div>
    </div>
  );
};

OnboardingForm.propTypes = {
  invite: PropTypes.object.isRequired
};

export { Onboarding };
